import React, { useState, useEffect } from 'react';
import './lms-deployment.css';
import CanvasIntegration from './CanvasIntegration';
import SchoologyIntegration from './SchoologyIntegration';
import GoogleClassroomIntegration from './GoogleClassroomIntegration';
import MoodleIntegration from './MoodleIntegration';
import BlackboardIntegration from './BlackboardIntegration';

const LMSDeployment = ({ tutorName = "Algebra Master Tutor", tutorId = "algebra-master", onDeploy }) => {
  const [activeTab, setActiveTab] = useState('lti');
  const [copied, setCopied] = useState(false);
  const [deployment, setDeployment] = useState({
    lmsType: 'canvas',
    courseId: '',
    deploymentOptions: {
      enableGrading: true,
      adaptToLearningStyles: true,
      enableBiasReporting: true,
      institutionalMode: false,
      tokenDisplayName: "Tokens", 
      showTokenBalanceInLms: true
    }
  });
  const [deploymentStatus, setDeploymentStatus] = useState("idle"); // idle, deploying, success, error
  const [lmsTypes, setLmsTypes] = useState(['canvas', 'schoology', 'blackboard', 'moodle', 'google_classroom']);
  const [courses, setCourses] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connectorId, setConnectorId] = useState(null);

  useEffect(() => {
    // Fetch LMS configuration and token settings
    fetchLmsConfig();
    fetchTokenSettings();
  }, []);

  // Function to fetch LMS configuration
  const fetchLmsConfig = async () => {
    try {
      const response = await fetch('/api/lms/config');
      const data = await response.json();
      if (data.lms_types) {
        setLmsTypes(data.lms_types);
      }
      // If we already have a connector, get courses
      if (data.connectors && data.connectors.length > 0) {
        setConnectorId(data.connectors[0]);
        fetchCourses(data.connectors[0]);
      }
    } catch (error) {
      console.error('Error fetching LMS config:', error);
    }
  };
  
  // Function to fetch token economy settings
  const fetchTokenSettings = async () => {
    try {
      const response = await fetch('/api/token/settings');
      const data = await response.json();
      
      // Update deployment options with token economy settings
      setDeployment(prev => ({
        ...prev,
        deploymentOptions: {
          ...prev.deploymentOptions,
          institutionalMode: data.institutional_mode || false,
          tokenDisplayName: data.token_display_name || "Tokens",
          showTokenBalanceInLms: data.show_token_balance_in_lms !== undefined ? 
            data.show_token_balance_in_lms : true
        }
      }));
    } catch (error) {
      console.error('Error fetching token settings:', error);
    }
  };

  // Function to fetch courses for a connected LMS
  const fetchCourses = async (connId) => {
    if (!connId) return;
    
    try {
      const response = await fetch(`/api/lms/courses?connector_id=${connId}`);
      const data = await response.json();
      if (data.courses) {
        setCourses(data.courses);
        // Set first course as default if available
        if (data.courses.length > 0) {
          setDeployment(prev => ({
            ...prev,
            courseId: data.courses[0].id
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Function to connect to LMS
  const connectToLms = async () => {
    try {
      const response = await fetch('/api/lms/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: deployment.lmsType,
          demo_mode: true // Demo mode for now
        })
      });
      
      const data = await response.json();
      if (data.success && data.connector_id) {
        setConnectorId(data.connector_id);
        fetchCourses(data.connector_id);
      } else {
        console.error('Failed to connect to LMS:', data.error);
      }
    } catch (error) {
      console.error('Error connecting to LMS:', error);
    }
  };

  // Function to handle deployment
  const handleDeploy = async () => {
    if (!connectorId || !deployment.courseId) {
      alert('Please connect to an LMS and select a course first');
      return;
    }
    
    setDeploymentStatus("deploying");
    
    try {
      // Save token economy settings first
      await updateTokenSettings({
        institutional_mode: deployment.deploymentOptions.institutionalMode,
        token_display_name: deployment.deploymentOptions.tokenDisplayName,
        show_token_balance_in_lms: deployment.deploymentOptions.showTokenBalanceInLms
      });
      
      // Then deploy the model with all settings
      const response = await fetch('/api/lms/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connector_id: connectorId,
          model_id: tutorId,
          course_id: deployment.courseId,
          options: deployment.deploymentOptions
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setDeploymentStatus("success");
        if (onDeploy) onDeploy(data);
      } else {
        setDeploymentStatus("error");
        alert(`Deployment failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentStatus("error");
    }
  };

  // Function to get LTI configuration
  const getLtiConfig = () => {
    // Get the display name for tokens/points based on institutional settings
    const tokenName = deployment.deploymentOptions.institutionalMode ? 
      (deployment.deploymentOptions.tokenDisplayName || "Progress Points") : 
      (deployment.deploymentOptions.tokenDisplayName || "Tokens");
      
    return `{
  "title": "Morpheus-RL: ${tutorName}",
  "description": "AI Math Tutoring with transparent reinforcement learning",
  "oidc_initiation_url": "https://morpheus-rl.ai/api/lms/lti/initiate",
  "target_link_uri": "https://morpheus-rl.ai/api/lms/lti/launch",
  "scopes": [
    "https://purl.imsglobal.org/spec/lti-ags/scope/lineitem",
    "https://purl.imsglobal.org/spec/lti-ags/scope/result.readonly",
    "https://purl.imsglobal.org/spec/lti-nrps/scope/contextmembership.readonly"
  ],
  "custom_fields": {
    "tutor_id": "${tutorId}",
    "tutor_name": "${tutorName}",
    "institutional_mode": "${deployment.deploymentOptions.institutionalMode}",
    "token_display_name": "${tokenName}",
    "show_token_balance": "${deployment.deploymentOptions.showTokenBalanceInLms}"
  }
}`;
  };

  // Function to get iframe code
  const getIframeCode = () => {
    // Get the display name for tokens/points based on institutional settings
    const tokenName = deployment.deploymentOptions.institutionalMode ? 
      (deployment.deploymentOptions.tokenDisplayName || "Progress Points") : 
      (deployment.deploymentOptions.tokenDisplayName || "Tokens");
      
    // Build query parameters for institutional settings
    const params = new URLSearchParams({
      institutional_mode: deployment.deploymentOptions.institutionalMode,
      token_display_name: tokenName,
      show_token_balance: deployment.deploymentOptions.showTokenBalanceInLms
    }).toString();
    
    return `<iframe 
  src="https://morpheus-rl.ai/embed/${tutorId}?${params}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  allow="microphone"
  title="${tutorName}">
</iframe>`;
  };

  // Function to copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle form field changes
  const handleChanges = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'lmsType') {
      setDeployment(prev => ({
        ...prev,
        lmsType: value
      }));
    } else if (name === 'courseId') {
      setDeployment(prev => ({
        ...prev,
        courseId: value
      }));
    } else if (name.startsWith('option_')) {
      const optionName = name.replace('option_', '');
      setDeployment(prev => ({
        ...prev,
        deploymentOptions: {
          ...prev.deploymentOptions,
          [optionName]: type === 'checkbox' ? checked : value
        }
      }));
      
      // Special handling for institutional mode changes
      if (optionName === 'institutionalMode') {
        // Update token display name when toggling institutional mode
        const newTokenName = checked ? "Progress Points" : "Tokens";
        
        // Only update if the user hasn't customized it already
        setDeployment(prevState => {
          const shouldUpdate = prevState.deploymentOptions.tokenDisplayName === "Tokens" || 
                              prevState.deploymentOptions.tokenDisplayName === "Progress Points";
          
          const updatedState = {
            ...prevState,
            deploymentOptions: {
              ...prevState.deploymentOptions,
              ...(!shouldUpdate ? {} : { tokenDisplayName: newTokenName })
            }
          };
          
          // Save these settings to the server
          updateTokenSettings({
            institutional_mode: checked,
            token_display_name: prevState.deploymentOptions.tokenDisplayName !== "Tokens" && 
                                prevState.deploymentOptions.tokenDisplayName !== "Progress Points" ? 
                                prevState.deploymentOptions.tokenDisplayName : newTokenName,
            show_token_balance_in_lms: prevState.deploymentOptions.showTokenBalanceInLms
          });
          
          return updatedState;
        });
      }
    }
  };
  
  // Function to update token economy settings on the server
  const updateTokenSettings = async (settings) => {
    try {
      await fetch('/api/token/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
    } catch (error) {
      console.error('Error updating token settings:', error);
    }
  };

  return (
    <div className="lms-deployment-container">
      <div className="lms-deployment-header">
        <h2>Deploy to Learning Management System</h2>
        <p>Make this tutor available in your school's LMS platform.</p>
      </div>
      
      <div className="lms-deployment-body">
        {/* LMS Selection */}
        <div className="lms-selection">
          <label htmlFor="lms-type">LMS Platform</label>
          <div className="selection-group">
            <select 
              id="lms-type" 
              name="lmsType" 
              value={deployment.lmsType} 
              onChange={handleChanges}
            >
              {lmsTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
            
            <button 
              onClick={connectToLms} 
              className="button-primary"
              disabled={connectorId !== null || deployment.lmsType === 'canvas' || deployment.lmsType === 'schoology' || deployment.lmsType === 'google_classroom' || deployment.lmsType === 'moodle' || deployment.lmsType === 'blackboard'}
            >
              {connectorId ? "Connected" : "Connect"}
            </button>
          </div>
        </div>
        
        {/* LMS Integration components - conditionally display based on selected LMS */}
        {deployment.lmsType === 'canvas' ? (
          <div className="canvas-integration-wrapper">
            <CanvasIntegration 
              user={null} // User prop will be passed from parent component
              onDeploy={(data) => {
                // When Canvas deploys a model, update our state
                setConnectorId(data?.connector_id || 'canvas-connector');
                if (onDeploy) onDeploy(data);
              }}
            />
          </div>
        ) : deployment.lmsType === 'schoology' ? (
          <div className="schoology-integration-wrapper">
            <SchoologyIntegration
              user={null}
              onDeploy={(data) => {
                setConnectorId(data?.connector_id || 'schoology-connector');
                if (onDeploy) onDeploy(data);
              }}
            />
          </div>
        ) : deployment.lmsType === 'google_classroom' ? (
          <div className="google-classroom-integration-wrapper">
            <GoogleClassroomIntegration
              user={null}
              onDeploy={(data) => {
                setConnectorId(data?.connector_id || 'google-classroom-connector');
                if (onDeploy) onDeploy(data);
              }}
            />
          </div>
        ) : deployment.lmsType === 'moodle' ? (
          <div className="moodle-integration-wrapper">
            <MoodleIntegration
              user={null}
              onDeploy={(data) => {
                setConnectorId(data?.connector_id || 'moodle-connector');
                if (onDeploy) onDeploy(data);
              }}
            />
          </div>
        ) : deployment.lmsType === 'blackboard' ? (
          <div className="blackboard-integration-wrapper">
            <BlackboardIntegration
              user={null}
              onDeploy={(data) => {
                setConnectorId(data?.connector_id || 'blackboard-connector');
                if (onDeploy) onDeploy(data);
              }}
            />
          </div>
        ) : (
          /* Course Selection (visible only after connecting) */
          connectorId && (
            <div className="course-selection">
              <label htmlFor="course-id">Course</label>
              <select 
                id="course-id" 
                name="courseId" 
                value={deployment.courseId} 
                onChange={handleChanges}
              >
                <option value="">Select a course...</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
          )
        )}
        
        {/* Deployment Options - Only show for generic LMS (not Canvas, Schoology, Google Classroom, Moodle, or Blackboard) */}
        {deployment.lmsType !== 'canvas' && deployment.lmsType !== 'schoology' && deployment.lmsType !== 'google_classroom' && deployment.lmsType !== 'moodle' && deployment.lmsType !== 'blackboard' && (
          <>
            <div className="deployment-options">
              <div className="options-header" onClick={() => setShowAdvanced(!showAdvanced)}>
                <h3>Deployment Options</h3>
                <span>{showAdvanced ? "▲" : "▼"}</span>
              </div>
              
              {showAdvanced && (
                <div className="options-body">
                  <div className="option-item">
                    <input 
                      type="checkbox" 
                      id="option-grading" 
                      name="option_enableGrading" 
                      checked={deployment.deploymentOptions.enableGrading} 
                      onChange={handleChanges}
                    />
                    <label htmlFor="option-grading">Enable grading integration</label>
                  </div>
                  
                  <div className="option-item">
                    <input 
                      type="checkbox" 
                      id="option-learning-styles" 
                      name="option_adaptToLearningStyles" 
                      checked={deployment.deploymentOptions.adaptToLearningStyles} 
                      onChange={handleChanges}
                    />
                    <label htmlFor="option-learning-styles">Adapt to student learning styles</label>
                  </div>
                  
                  <div className="option-item">
                    <input 
                      type="checkbox" 
                      id="option-bias" 
                      name="option_enableBiasReporting" 
                      checked={deployment.deploymentOptions.enableBiasReporting} 
                      onChange={handleChanges}
                    />
                    <label htmlFor="option-bias">Enable bias reporting</label>
                  </div>
                  
                  <div className="options-divider"></div>
                  <h4 className="option-section-title">Token Economy Settings</h4>
                  
                  <div className="option-item">
                    <input 
                      type="checkbox" 
                      id="option-institutional-mode" 
                      name="option_institutionalMode" 
                      checked={deployment.deploymentOptions.institutionalMode} 
                      onChange={handleChanges}
                    />
                    <label htmlFor="option-institutional-mode">Use institutional mode (rename tokens to points)</label>
                  </div>
                  
                  <div className="option-item">
                    <input 
                      type="checkbox" 
                      id="option-show-balance" 
                      name="option_showTokenBalanceInLms" 
                      checked={deployment.deploymentOptions.showTokenBalanceInLms} 
                      onChange={handleChanges}
                    />
                    <label htmlFor="option-show-balance">
                      Show {deployment.deploymentOptions.institutionalMode ? 'point' : 'token'} balance in LMS
                    </label>
                  </div>
                  
                  <div className="option-item token-display-name">
                    <label htmlFor="token-display-name">Display name for tokens/points:</label>
                    <input 
                      type="text" 
                      id="token-display-name" 
                      name="option_tokenDisplayName" 
                      value={deployment.deploymentOptions.tokenDisplayName} 
                      onChange={handleChanges}
                      placeholder={deployment.deploymentOptions.institutionalMode ? "Progress Points" : "Tokens"}
                      className="token-name-input"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Integration Tabs - Only show for non-Canvas LMS */}
            <div className="integration-tabs">
              <div className="tabs-header">
                <button 
                  className={activeTab === 'lti' ? 'active' : ''}
                  onClick={() => setActiveTab('lti')}
                >
                  LTI Integration
                </button>
                <button 
                  className={activeTab === 'iframe' ? 'active' : ''}
                  onClick={() => setActiveTab('iframe')}
                >
                  Embed Code
                </button>
                <button 
                  className={activeTab === 'api' ? 'active' : ''}
                  onClick={() => setActiveTab('api')}
                >
                  API Integration
                </button>
              </div>
            </div>
          </>
        )}
        
        {/* Canvas, Schoology, Google Classroom, Moodle, and Blackboard don't use the integration tabs */}
        {deployment.lmsType !== 'canvas' && deployment.lmsType !== 'schoology' && deployment.lmsType !== 'google_classroom' && deployment.lmsType !== 'moodle' && deployment.lmsType !== 'blackboard' && (
          
          <div className="tabs-content">
            {/* LTI Tab */}
            {activeTab === 'lti' && (
              <div className="tab-pane">
                <div className="info-box">
                  <p><strong>LTI Integration</strong> is the recommended method for educational institutions.</p>
                  <p>It provides seamless integration with grade passback and user management.</p>
                </div>
                
                <div className="lti-config">
                  <label>LTI Configuration JSON</label>
                  <div className="code-container">
                    <pre>{getLtiConfig()}</pre>
                    <button 
                      className="copy-button"
                      onClick={() => copyToClipboard(getLtiConfig())}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  
                  <div className="lti-instructions">
                    <h4>Setup Instructions</h4>
                    <ol>
                      <li>In your LMS, navigate to <strong>Settings &gt; Apps</strong></li>
                      <li>Click <strong>Add App &gt; By Configuration URL</strong></li>
                      <li>Enter <code>https://morpheus-rl.ai/api/lms/lti/config</code></li>
                      <li>Click <strong>Submit</strong></li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
            
            {/* iFrame Tab */}
            {activeTab === 'iframe' && (
              <div className="tab-pane">
                <div className="info-box info-box-warning">
                  <p><strong>Simple Alternative:</strong> Embed codes work in most LMS content areas but provide limited integration capabilities.</p>
                </div>
                
                <div className="iframe-config">
                  <label>Embed Code</label>
                  <div className="code-container">
                    <pre>{getIframeCode()}</pre>
                    <button 
                      className="copy-button"
                      onClick={() => copyToClipboard(getIframeCode())}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  
                  <div className="iframe-preview">
                    <h4>Preview</h4>
                    <div className="preview-container">
                      <div className="preview-placeholder">
                        <h3>{tutorName}</h3>
                        <p>AI tutor will appear here when embedded in your LMS</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* API Tab */}
            {activeTab === 'api' && (
              <div className="tab-pane">
                <div className="info-box info-box-advanced">
                  <p><strong>Advanced Integration:</strong> API connections enable deeper integration with your LMS but require developer support.</p>
                </div>
                
                <div className="api-config">
                  <div className="config-item">
                    <label>API Key</label>
                    <div className="input-with-copy">
                      <input type="text" readOnly value="mrl_api_6b9d2c5a7b1e6f3d4s5a8f7d9c6b3a2e" />
                      <button 
                        className="copy-button"
                        onClick={() => copyToClipboard("mrl_api_6b9d2c5a7b1e6f3d4s5a8f7d9c6b3a2e")}
                      >
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                  
                  <div className="config-item">
                    <label>Endpoint URL</label>
                    <div className="input-with-copy">
                      <input type="text" readOnly value={`https://morpheus-rl.ai/api/v1/tutors/${tutorId}`} />
                      <button 
                        className="copy-button"
                        onClick={() => copyToClipboard(`https://morpheus-rl.ai/api/v1/tutors/${tutorId}`)}
                      >
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                  
                  <div className="api-buttons">
                    <button className="button-secondary">View API Documentation</button>
                    <button className="button-secondary">Configure Webhook</button>
                  </div>
                  
                  <div className="api-example">
                    <h4>Example API Request</h4>
                    <pre>{`curl --request POST \\
  --url https://morpheus-rl.ai/v1/tutors/${tutorId}/chat \\
  --header 'Authorization: Bearer mrl_api_6b9d2c5a7b1e6f3d4s5a8f7d9c6b3a2e' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "student_id": "student_12345",
    "question": "Can you help me solve x^2 - 5x + 6 = 0?",
    "context": {
      "course_id": "math101",
      "unit": "quadratics",
      "previous_interactions": 3
    },
    "settings": {
      "institutional_mode": ${deployment.deploymentOptions.institutionalMode},
      "token_display_name": "${deployment.deploymentOptions.tokenDisplayName}",
      "show_token_balance": ${deployment.deploymentOptions.showTokenBalanceInLms}
    }
  }'`}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer is different for LMS platforms with custom integration components */}
      <div className="lms-deployment-footer">
        <button className="button-secondary">Back to Tutors</button>
        {deployment.lmsType !== 'canvas' && deployment.lmsType !== 'schoology' && deployment.lmsType !== 'google_classroom' && deployment.lmsType !== 'moodle' && deployment.lmsType !== 'blackboard' ? (
          <button 
            className="button-primary"
            onClick={handleDeploy}
            disabled={!connectorId || !deployment.courseId || deploymentStatus === "deploying" || deploymentStatus === "success"}
          >
            {deploymentStatus === "deploying" ? (
              <span>Deploying...</span>
            ) : deploymentStatus === "success" ? (
              <span>Deployed</span>
            ) : (
              "Deploy to LMS"
            )}
          </button>
        ) : null /* Canvas and Schoology components have their own deploy buttons */}
      </div>
    </div>
  );
};

export default LMSDeployment;